import {
    getAttendeeTimezone,
    getFormattedDueDate as formatDueDate,
    getFormattedEventDate as formatEventDate,
    shouldShowDateInAttendeeTimezone,
    TimezoneLocale,
} from '@eventbrite/event-date-time';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { getCountryNameFromTwoLetterCode } from '@eventbrite/intl';
import {
    CAPACITY_WAITLIST,
    CTA_CHECKOUT_TEXT,
    CTA_COMPLETE_FREE_TEXT,
    PrivateSetting,
} from '../constants';
import { EventStatus } from '../constants/eventStatus';
import { CheckoutState } from '../state';
import { EventNamespace } from '../state/event';
import { getEventDatesFormat } from '../utils/event';
import { createSelector, getNamespaceValue } from './utils/selector';

/**
 * @return `true` if the `userLocale` is determined to be the same as the country.
 */
const _isLocaleInCountry = (country: string, userLocale: string): boolean => {
    if (userLocale && country) {
        return userLocale.slice(-2).toLowerCase() === country.toLowerCase();
    }

    return false;
};

/**
 * Return a formatted string representing the event's location. If the user's locale is
 * the same as the country for the event venue, don't display the country to the user.
 *
 * @param localizedAreaDisplay The area information for the event venue location. Example: "Palm Springs, CA"
 * @param country The country code for the event venue location. Example: "US"
 * @param userLocale The locale of the browser. Example: "en_US"
 * @return Formatted location string. Example: "Palm Springs, CA, United States"
 */
export const _getFormattedEventLocation = (
    eventInfo: { localizedAreaDisplay?: string; country?: string } = {},
    userLocale: string,
): string | GenericLazyString => {
    const { localizedAreaDisplay, country } = eventInfo;

    if (localizedAreaDisplay && country) {
        const localizedCountryName = getCountryNameFromTwoLetterCode(country);

        // Translators: The 'Area' is the full city name. For example, 'Palm Springs, CA'.
        const localizedAreaWithCountry = gettext(
            '%(localizedAreaDisplay)s, %(localizedCountryName)s',
            {
                localizedAreaDisplay,
                localizedCountryName,
            },
        );

        return _isLocaleInCountry(country, userLocale)
            ? localizedAreaDisplay
            : localizedAreaWithCountry;
    }

    return '';
};

export const getEventId = (state: CheckoutState) =>
    getNamespaceValue(state?.event, 'id');

export const getEventTitle = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'name.text', '');

export const getIsEventOnline = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'onlineEvent', false);

/**
 * Selector that can be used to retrieve the full event date range string to display
 */
export const getFormattedEventDate = createSelector(
    (state: CheckoutState) => state,
    (state: CheckoutState) => state.app.locale,
    ((state: CheckoutState, userLocale: string) => {
        // For online events we should show the attendee timezone
        const useTimezoneFrom = shouldShowDateInAttendeeTimezone(
            getIsEventOnline(state),
            getAttendeeTimezone(),
        )
            ? TimezoneLocale.Attendee // Attendee for onLine events
            : TimezoneLocale.Creator; // Creator for onsite events

        const event = state.event as EventNamespace;

        return formatEventDate(
            event,
            userLocale,
            getEventDatesFormat(event, userLocale, useTimezoneFrom),
            useTimezoneFrom,
        );
    }) as unknown as any,
);

/**
 * Selector that can be used to retrieve the due payment date string to display
 */
export const getFormattedDueDate = createSelector(
    (state: CheckoutState) => state.event,
    (state: CheckoutState) => state.app.locale,
    (state: CheckoutState) => state.app.deferredPaymentDueDays,
    ((
        event: EventNamespace,
        userLocale: string,
        deferredPaymentDueDays: number,
    ) =>
        formatDueDate(
            event,
            userLocale,
            deferredPaymentDueDays,
        )) as unknown as any,
);

/**
 * Selector that can be used to retrieve the event's location formatted for user's locale.
 */
export const getFormattedEventLocation = createSelector(
    (state: CheckoutState) => getNamespaceValue(state.event, 'venue.address'),
    (state: CheckoutState) => state.app.locale,
    ((
        address: {
            country: string;
            localizedAreaDisplay: string;
        },
        userLocale: string,
    ) => _getFormattedEventLocation(address, userLocale)) as unknown as any,
);

/**
 * Selector that can be used to retrieve the event's reserved seating prop
 */
export const getIsReservedSeating = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'isReservedSeating', false);

export const getIsDraftEvent = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'status') === EventStatus.draft;

export const getCheckoutGroups = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'checkoutInfo.checkoutGroups', []);

export const getEventSeatMapThumbnailSrc = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'properties.seatmapThumbnailUrl');

export const getEventShippingSettings = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'shippingSettings');

/**
 * Selector that can be used to know if the current event is protected
 * An event is considered protected if isProtected is true, and privacySetting equals invite_only or password_protected
 * Password_expired is a special case that exists only on UI side: it is not returned by the BE, and is set via UPDATE_EVENT_PRIVACY_SETTING action when the event's password
 * is either changed by the organizer while the user is buying, or when the password has expired.
 */
export const getIsEventProtected = (state: CheckoutState) => {
    const privacySetting = getNamespaceValue(state.event, 'privacySetting');
    const isProtected = getNamespaceValue(
        state.event,
        'properties.isProtected',
    );

    return Boolean(
        isProtected &&
            [
                PrivateSetting.passwordExpired,
                PrivateSetting.passwordRequired,
                PrivateSetting.invitationRequired,
            ].includes(privacySetting),
    );
};

/**
 * Selector that can be used to return the group type in case of group registration
 */
export const getEventGroupType = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'groupSettings.groupType');

export const getEventImageSrc = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'properties.image') ?? null;

export const getWaitlistTicketId = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'waitlistSettings.ticketId') ?? null;

/**
 * @return `true` when waitlist is enabled and available for new entries, regardless of waitlist type.
 */
export const getIsWaitlistAvailable = (state: CheckoutState) =>
    !getNamespaceValue(state.event, 'waitlistSettings.full', true) ?? false;

/**
 * @note On its own, does not necessarily mean the waitlist *should* be shown as
 * this method does not check each ticket's status.
 *
 * @return `true` when waitlist is enabled, available for new entries and it's using a specific ticket as trigger.
 */
export const getHasTicketLevelWaitlist = (state: CheckoutState) =>
    getIsWaitlistAvailable(state) &&
    getNamespaceValue(state.event, 'waitlistSettings.ticketId') !==
        CAPACITY_WAITLIST;

export const getEventGroupSettings = (state: CheckoutState) => {
    const {
        teamPasswordIncluded: hasPassword,
        teamPasswordRequired: isPasswordRequired,
        teamPstIncluded: hasPreferredStartTime,
        teamPstRequired: isPreferredStartTimeRequired,
        teamPstChoices: preferredStartTimeChoices,
    } = getNamespaceValue(state.event, 'groupSettings') || {
        hasPassword: null,
        isPasswordRequired: null,
        hasPreferredStartTime: null,
        isPreferredStartTimeRequired: null,
        preferredStartTimeChoices: null,
    };

    return {
        hasPassword,
        isPasswordRequired,
        hasPreferredStartTime,
        isPreferredStartTimeRequired,
        preferredStartTimeChoices,
    };
};

export const getIsNewlyCreatedGroup = (state: CheckoutState) =>
    getNamespaceValue(
        state.groupRegistration,
        'teamSettings.isNewlyCreatedGroup',
    );

export const getTeamSettingsErrors = (state: CheckoutState) =>
    getNamespaceValue(state.groupRegistration, 'teamSettings.errors');

export const getTeamSettings = (state: CheckoutState) =>
    getNamespaceValue(state.groupRegistration, 'teamSettings');

export const getCurrency = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'currency');

export const getEventUrl = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'url');

export const getEventCountryCode = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'venue.address.country', '');

export const getIsFreeEvent = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'isFree');

export const getTicketSelectDefaultCtaText = (state: CheckoutState) =>
    getIsFreeEvent(state) ? CTA_COMPLETE_FREE_TEXT : CTA_CHECKOUT_TEXT;

export const hasRequiredCannedQuestions = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'hasRequiredCannedQuestions');

export const hasRequiredCustomQuestions = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'hasRequiredCustomQuestions');

export const getSurveyType = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'surveyType');

export const hasSpecialInstructions = (state: CheckoutState) =>
    !!getNamespaceValue(state.event, 'surveyInfo');

export const getEventSalesStatus = (state: CheckoutState) => ({
    eventSalesStatus: getNamespaceValue(state.event, 'eventSalesStatus'),
});
